/**
 * GlobalUploadProgress
 *
 * A fixed-position floating widget that shows upload progress across all
 * knowledge bases.  Visible whenever the global upload store has active
 * or recently-completed entries.
 */

import { useTranslate } from '@/hooks/common-hooks';
import {
  UploadEntryMeta,
  selectEntryCount,
  selectHasActiveUploads,
  useUploadStore,
} from '@/stores/upload-store';
import { getFileValidationFailureMessage } from '@/utils/file-validation';
import { UploadQueueState, UploadTask } from '@/utils/upload-queue';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DownOutlined,
  FolderOpenOutlined,
  LoadingOutlined,
  MinusOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Progress, Space, Tag, Tooltip, Typography } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './index.less';

const { Text } = Typography;

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatSpeed = (speed: number): string => {
  if (speed <= 0) return '-';
  return `${speed.toFixed(2)} MB/s`;
};

const FILE_LIST_PREVIEW_COUNT = 5;

/* ------------------------------------------------------------------ */
/*  Per-file row (memo: task ref changes when progress updates)         */
/* ------------------------------------------------------------------ */
const FileRow = React.memo<{
  task: UploadTask;
  t: (key: string) => string;
  onRemove?: (fileId: string) => void;
}>(({ task, t, onRemove }) => {
  const isCorrupted =
    task.status === 'rejected' && task.errorCode === 'FILE_CORRUPTED';
  const isFormatInvalid =
    task.status === 'rejected' && task.errorCode === 'FILE_FORMAT_INVALID';
  const parseEnqueueFailed =
    task.status === 'success' &&
    task.parseRequested &&
    task.parseEnqueued === false;
  const parseEnqueueUncertain =
    task.status === 'success' &&
    task.parseRequested &&
    task.parseEnqueued === null;
  const parseCauseLabelKeys: Record<string, string> = {
    FILE_ENCRYPTED: 'encryptedFile',
    FILE_PARSE_EMPTY: 'emptyFile',
    FILE_PARSE_FAILED: 'parseFailed',
  };
  const parseCauseLabelKey = task.causeCode
    ? parseCauseLabelKeys[task.causeCode]
    : undefined;
  const parseFailureTip = getFileValidationFailureMessage({
    error: task.error,
    error_code: task.errorCode,
    cause_code: task.causeCode,
    parse_enqueued: task.parseEnqueued,
  });
  const statusIcon =
    task.status === 'uploading' || task.status === 'retrying' ? (
      <LoadingOutlined className={styles.statusIcon} data-status="uploading" />
    ) : parseEnqueueFailed || parseEnqueueUncertain ? (
      <CheckCircleOutlined
        className={styles.statusIcon}
        data-status="success"
      />
    ) : task.status === 'success' ? (
      <CheckCircleOutlined
        className={styles.statusIcon}
        data-status="success"
      />
    ) : task.status === 'skipped' ? (
      <StopOutlined className={styles.statusIcon} data-status="cancelled" />
    ) : task.status === 'error' || task.status === 'rejected' ? (
      <CloseCircleOutlined className={styles.statusIcon} data-status="error" />
    ) : task.status === 'cancelled' ? (
      <StopOutlined className={styles.statusIcon} data-status="cancelled" />
    ) : (
      <CloudUploadOutlined
        className={styles.statusIcon}
        data-status="pending"
      />
    );

  const statusTagMap: Record<string, { color: string; label: string }> = {
    pending: { color: 'default', label: t('pending') || 'Pending' },
    uploading: { color: 'processing', label: t('uploading') || 'Uploading' },
    success: { color: 'success', label: t('success') || 'Success' },
    skipped: { color: 'warning', label: t('duplicateSkipped') || 'Duplicate' },
    error: { color: 'error', label: t('failed') || 'Failed' },
    rejected: {
      color: 'error',
      label: isCorrupted
        ? t('corruptedRejected') || 'Corrupt rejected'
        : isFormatInvalid
          ? t('formatInvalidRejected') || 'Invalid format rejected'
          : t('rejected') || 'Rejected',
    },
    cancelled: { color: 'default', label: t('cancelled') || 'Cancelled' },
    retrying: { color: 'warning', label: t('retrying') || 'Retrying' },
  };
  const tag = parseEnqueueFailed
    ? {
        color: 'warning',
        label: parseCauseLabelKey
          ? t(parseCauseLabelKey)
          : t('parseEnqueueFailed') || 'Parse queue failed',
      }
    : parseEnqueueUncertain
      ? {
          color: 'warning',
          label: t('parseEnqueueUncertain') || 'Parse status uncertain',
        }
      : statusTagMap[task.status] || statusTagMap.pending;

  return (
    <div className={styles.fileRow}>
      {/* icon + name */}
      <span className={styles.fileRowIcon}>{statusIcon}</span>
      <Text
        ellipsis={{ tooltip: task.file?.name }}
        className={styles.fileRowName}
      >
        {task.file?.name}
      </Text>

      {/* size */}
      <span className={styles.fileRowCell}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {formatBytes(task.totalBytes)}
        </Text>
      </span>

      {/* progress or speed */}
      <span className={styles.fileRowCell}>
        {task.status === 'uploading' ? (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {Math.round(task.progress)}%{' '}
            {task.speed > 0 ? formatSpeed(task.speed) : ''}
          </Text>
        ) : task.status === 'skipped' ? (
          <Tooltip
            title={
              t('duplicateSkippedTip') ||
              'The same file already exists in this knowledge base and was not uploaded again'
            }
          >
            <Tag
              color={tag.color}
              style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}
            >
              {tag.label}
            </Tag>
          </Tooltip>
        ) : isCorrupted || isFormatInvalid ? (
          <Tooltip
            title={
              isFormatInvalid
                ? t('formatInvalidRejectedTip') ||
                  'The file extension does not match its content, or the file is damaged'
                : t('corruptedRejectedTip') ||
                  'The file is corrupted or incomplete and was rejected'
            }
          >
            <Tag
              color={tag.color}
              style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}
            >
              {tag.label}
            </Tag>
          </Tooltip>
        ) : parseEnqueueFailed ? (
          <Tooltip
            title={
              parseFailureTip ||
              t('parseEnqueueFailedTip') ||
              'Upload succeeded, but automatic parsing failed to queue'
            }
          >
            <Tag
              color={tag.color}
              style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}
            >
              {tag.label}
            </Tag>
          </Tooltip>
        ) : parseEnqueueUncertain ? (
          <Tooltip
            title={
              parseFailureTip ||
              t('parseEnqueueUncertainTip') ||
              'Upload succeeded with an uncertain automatic parsing state'
            }
          >
            <Tag
              color={tag.color}
              style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}
            >
              {tag.label}
            </Tag>
          </Tooltip>
        ) : (
          <Tag
            color={tag.color}
            style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}
          >
            {tag.label}
          </Tag>
        )}
      </span>

      {/* remove button for error/cancelled */}
      <span className={styles.fileRowAction}>
        {(task.status === 'error' ||
          task.status === 'rejected' ||
          task.status === 'cancelled') &&
          onRemove && (
            <Tooltip title={t('remove') || 'Remove'}>
              <CloseOutlined
                className={styles.removeIcon}
                onClick={() => onRemove(task.fileId)}
              />
            </Tooltip>
          )}
      </span>
    </div>
  );
});

/* ------------------------------------------------------------------ */
/*  Per-batch section (one per upload batch, same kb can have multiple) */
/* ------------------------------------------------------------------ */
const KbUploadSection: React.FC<{
  entryId: string;
  state: UploadQueueState;
  meta?: UploadEntryMeta;
}> = ({ entryId, state, meta }) => {
  const { t } = useTranslate('fileManager');
  const queryClient = useQueryClient();
  const cancelAll = useUploadStore((s) => s.cancelAll);
  const retryFailed = useUploadStore((s) => s.retryFailed);
  const removeFile = useUploadStore((s) => s.removeFile);
  const resetQueue = useUploadStore((s) => s.resetQueue);
  const [fileListExpanded, setFileListExpanded] = useState(false);

  const handleRemove = useCallback(
    (fileId: string) => removeFile(entryId, fileId),
    [entryId, removeFile],
  );

  const {
    tasks,
    totalFiles,
    uploadedFiles,
    totalBytes,
    uploadedBytes,
    isUploading,
    failedCount,
    skippedCount,
  } = state;

  // Size-based progress
  const sizePercent =
    totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;

  const kbDisplayName = meta?.kbName || entryId;
  const showExpandToggle = tasks.length > FILE_LIST_PREVIEW_COUNT;
  const displayedTasks = fileListExpanded
    ? tasks
    : tasks.slice(0, FILE_LIST_PREVIEW_COUNT);
  const hasRetryableFailures = tasks.some(
    (task) =>
      task.status === 'error' &&
      task.retryable !== false &&
      (task.errorCode !== 'FILE_VALIDATION_TIMEOUT' ||
        (task.retryCount || 0) < 1),
  );

  return (
    <div className={styles.kbSection}>
      {/* ---- KB header: name + count + actions (same line) ---- */}
      <div className={styles.kbHeader}>
        <div className={styles.kbHeaderLeft}>
          <FolderOpenOutlined className={styles.kbIcon} />
          <Text
            strong
            style={{ fontSize: 13 }}
            ellipsis={{ tooltip: kbDisplayName }}
          >
            {kbDisplayName}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
            ({uploadedFiles}/{totalFiles})
            {failedCount > 0 && (
              <>
                {' · '}
                <Text type="danger" style={{ fontSize: 12 }}>
                  {failedCount} {t('failed') || 'failed'}
                </Text>
              </>
            )}
            {skippedCount > 0 && (
              <>
                {' · '}
                <Text type="warning" style={{ fontSize: 12 }}>
                  {skippedCount}{' '}
                  {t('duplicatesSkipped') || 'duplicate file(s) skipped'}
                </Text>
              </>
            )}
          </Text>
        </div>
        <Space size={2}>
          {hasRetryableFailures && !isUploading && (
            <Tooltip title={t('retryFailed') || 'Retry Failed'}>
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => retryFailed(entryId, queryClient)}
              />
            </Tooltip>
          )}
          {isUploading && (
            <Tooltip title={t('cancelUpload') || 'Cancel'}>
              <Button
                type="text"
                size="small"
                danger
                icon={<StopOutlined />}
                onClick={() => cancelAll(entryId)}
              />
            </Tooltip>
          )}
          {!isUploading && (
            <Tooltip title={t('remove') || 'Clear'}>
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => resetQueue(entryId)}
              />
            </Tooltip>
          )}
        </Space>
      </div>

      {/* ---- size-based progress bar (with size/speed in tooltip) ---- */}
      <Tooltip
        title={
          `${formatBytes(uploadedBytes)} / ${formatBytes(totalBytes)}` +
          (state.averageSpeed > 0 && isUploading
            ? ` · ${formatSpeed(state.averageSpeed)}`
            : '')
        }
      >
        <div>
          <Progress
            percent={sizePercent}
            size="small"
            status={
              isUploading ? 'active' : failedCount > 0 ? 'exception' : 'success'
            }
            style={{ marginBottom: 4 }}
          />
        </div>
      </Tooltip>

      {/* ---- file table header ---- */}
      <div className={`${styles.fileRow} ${styles.fileRowHeader}`}>
        <span className={styles.fileRowIcon} />
        <Text
          type="secondary"
          className={styles.fileRowName}
          style={{ fontSize: 11 }}
        >
          {t('fileName') || 'File'}
        </Text>
        <span className={styles.fileRowCell}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t('size') || 'Size'}
          </Text>
        </span>
        <span className={styles.fileRowCell}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t('status') || 'Status'}
          </Text>
        </span>
        <span className={styles.fileRowAction} />
      </div>

      {/* ---- file rows ---- */}
      <div className={styles.fileListScroll}>
        {displayedTasks.map((task) => (
          <FileRow
            key={task.fileId}
            task={task}
            t={t}
            onRemove={handleRemove}
          />
        ))}
        {showExpandToggle && (
          <Button
            type="link"
            size="small"
            style={{ padding: '2px 0', fontSize: 11, height: 'auto' }}
            onClick={() => setFileListExpanded((v) => !v)}
          >
            {fileListExpanded
              ? t('collapseFileList') || 'Collapse'
              : (t('expandFileList') || 'Expand ({{count}} files)').replace(
                  '{{count}}',
                  String(tasks.length),
                )}
          </Button>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
const GlobalUploadProgress: React.FC = () => {
  const { t } = useTranslate('fileManager');
  const queueStates = useUploadStore((s) => s.queueStates);
  const entryMeta = useUploadStore((s) => s.entryMeta);
  const hasActive = useUploadStore(selectHasActiveUploads);
  const entryCount = useUploadStore(selectEntryCount);
  const clearFinished = useUploadStore((s) => s.clearFinished);

  const [expanded, setExpanded] = useState(false);

  // Auto-expand when a new upload starts
  const prevHasActive = useRef(hasActive);
  useEffect(() => {
    if (hasActive && !prevHasActive.current) {
      setExpanded(true);
    }
    prevHasActive.current = hasActive;
  }, [hasActive]);

  if (entryCount === 0) return null;

  // Aggregate across all KBs
  const totalFiles = Object.values(queueStates).reduce(
    (n, s) => n + s.totalFiles,
    0,
  );
  const uploadedFiles = Object.values(queueStates).reduce(
    (n, s) => n + s.uploadedFiles,
    0,
  );
  const totalBytes = Object.values(queueStates).reduce(
    (n, s) => n + s.totalBytes,
    0,
  );
  const uploadedBytes = Object.values(queueStates).reduce(
    (n, s) => n + s.uploadedBytes,
    0,
  );

  const badgeLabel = hasActive
    ? `${t('uploading') || 'Uploading'} ${formatBytes(uploadedBytes)}/${formatBytes(totalBytes)}`
    : `${t('uploadComplete') || 'Done'} ${uploadedFiles}/${totalFiles}`;

  return (
    <div className={styles.globalUploadWrap}>
      {expanded ? (
        /* ========== EXPANDED PANEL ========== */
        <div className={`${styles.expandedPanel} ${styles.fadeIn}`}>
          {/* header */}
          <div className={styles.panelHeader}>
            <div className={styles.panelHeaderTop}>
              <Space size={8}>
                <CloudUploadOutlined />
                <Text strong>{t('uploadTask') || 'Upload Tasks'}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {hasActive
                    ? `${t('uploading') || 'Uploading'} ${formatBytes(uploadedBytes)}/${formatBytes(totalBytes)}`
                    : `${uploadedFiles}/${totalFiles} ${t('uploadComplete') || 'done'}`}
                </Text>
              </Space>
              <Space size={0}>
                {!hasActive && (
                  <Tooltip title={t('clearAll') || 'Clear all'}>
                    <Button
                      type="text"
                      size="small"
                      onClick={clearFinished}
                      icon={<CloseCircleOutlined />}
                    />
                  </Tooltip>
                )}
                <Tooltip title={t('minimize') || 'Minimize'}>
                  <Button
                    type="text"
                    size="small"
                    onClick={() => setExpanded(false)}
                    icon={<MinusOutlined />}
                  />
                </Tooltip>
              </Space>
            </div>
            <div className={styles.uploadHint}>{t('uploadHint')}</div>
          </div>

          {/* body */}
          <div className={styles.panelBody}>
            {Object.entries(queueStates).map(([entryId, state]) => (
              <KbUploadSection
                key={entryId}
                entryId={entryId}
                state={state}
                meta={entryMeta[entryId]}
              />
            ))}
          </div>
        </div>
      ) : (
        /* ========== COLLAPSED BADGE ========== */
        <div
          className={`${styles.collapsedBadge} ${styles.fadeIn}`}
          onClick={() => setExpanded(true)}
        >
          <Badge status={hasActive ? 'processing' : 'success'} />
          <Text style={{ fontSize: 13 }}>{badgeLabel}</Text>
          <DownOutlined className={styles.expandIcon} />
        </div>
      )}
    </div>
  );
};

export default GlobalUploadProgress;
